/**
 * payables · fornecedores (spec 03 §9 · tela 6.6 · DS-01).
 *
 * **A chave é a CAPACIDADE, nunca a Empresa.** Todo `id` deste contrato é o uuid de
 * `capacidade_fornecedor` — o mesmo que `conta_pagar.fornecedor_capacidade_id`
 * aponta e que `6.6?id=` resolve. Usar o id da empresa pareceria funcionar até o
 * primeiro fornecedor pessoa física: a PF não tem empresa nenhuma, e ficaria
 * inendereçável na própria tela que existe para pagá-la.
 *
 * O que este contrato NÃO tem, e a ausência é a decisão:
 *
 * - **Nenhum campo de Setor nem de Tipo de empresa** (R6 · D-32 · DS-64). Os dois são
 *   atributos de quem a DV FATURA; a Empresa criada pela 6.6 nasce com
 *   `tipo_empresa_id NULL`. O que classifica o fornecedor é a **Categoria** da
 *   capacidade, que é campo próprio desta tela.
 * - **Tarefas usam a API própria do passo 9.4.** A ficha monta o bloco compartilhado
 *   de tarefas relacionadas; elas não são campos deste contrato de fornecedor.
 * - **Nenhum valor bruto para quem não pode vê-lo.** Valor de despesa e valor mensal
 *   de contrato são `nullable`, e `null` quer dizer "este nível conta registros mas
 *   não vê montante" — o mesmo idioma do cockpit (`indicadorDoCockpit.valor`).
 */
import { z } from 'zod'
import {
  entradaDeNovaEmpresa,
  entradaDeNovoContato,
  metodoDePagamento,
  type MetodoDePagamento,
} from './cadastro.js'
import { dataNominal, meta, uuid } from './comum.js'
import { endereco } from './entidades.js'

/**
 * A natureza é DERIVADA da parte, nunca digitada como dado: contato → PF; empresa
 * com país BR → PJ; empresa fora do BR → Exterior. Ela governa a máscara do
 * documento e o banner de RPA, e por isso viaja pronta em vez de a tela recalcular.
 */
export const naturezaDeFornecedor = z.enum(['PJ', 'PF', 'Exterior'])
export type NaturezaDeFornecedor = z.infer<typeof naturezaDeFornecedor>

export const parteDoFornecedor = z.enum(['empresa', 'contato'])
export type ParteDoFornecedor = z.infer<typeof parteDoFornecedor>

/*
  A enum mora no CADASTRO, e é reexportada aqui.

  Ela descreve `perfil_pagamento.metodo`, que é coluna do perfil — e o perfil é do
  cadastro, não da 6.6. Deixá-la aqui foi o que permitiu a 3.3 gravar o perfil sem
  método: quem escreveu a rota do cadastro não tinha o conceito à mão.
*/
export { metodoDePagamento, type MetodoDePagamento }

export const ROTULO_DO_METODO: Readonly<Record<MetodoDePagamento, string>> = {
  pix: 'PIX',
  ted: 'Transferência (TED)',
  boleto: 'Boleto',
  cambio: 'Câmbio internacional',
}

/** O rótulo do campo de chave muda com o método — lei do protótipo 6.6. */
export const ROTULO_DA_CHAVE: Readonly<Record<MetodoDePagamento, string>> = {
  pix: 'Chave PIX',
  ted: 'Banco · agência · conta',
  boleto: 'Referência do boleto',
  cambio: 'IBAN + SWIFT',
}

/** A linha da lista à esquerda: identidade e nada mais (sem dinheiro — D-43). */
export const linhaDeFornecedor = z.object({
  id: uuid,
  nome: z.string(),
  parteTipo: parteDoFornecedor,
  natureza: naturezaDeFornecedor,
  pais: z.string(),
  /** O documento da parte, inteiro e normalizado: o CNPJ da empresa ou o documento da pessoa (D-149). */
  documento: z.string().nullable(),
  categoria: z.string().nullable(),
})
export type LinhaDeFornecedor = z.infer<typeof linhaDeFornecedor>

/**
 * O aviso de cadastro compartilhado: a mesma parte também é Cliente/Anunciante ou
 * Parceiro comissionado. Não é decoração — é o que explica por que o documento e o
 * endereço não se editam aqui.
 */
export const outroPapelDaParte = z.object({
  papel: z.enum(['empresa', 'parceiro']),
  rotulo: z.string(),
  /** Para o link 3.2 — `null` na PF, que não tem ficha de Empresa. */
  empresaId: uuid.nullable(),
})
export type OutroPapelDaParte = z.infer<typeof outroPapelDaParte>

export const perfilDePagamentoDoFornecedor = z.object({
  id: uuid,
  /** O rótulo do perfil na parte ("Pagamento do fornecedor", "Conta principal"…). */
  rotulo: z.string(),
  /** É o perfil PRINCIPAL da parte? A ficha avisa, porque editá-lo vale para todo pagamento. */
  principal: z.boolean(),
  /** `null` = perfil anterior ao 7.4 cujo método não pôde ser inferido. */
  metodo: metodoDePagamento.nullable(),
  /** Já montada pelo servidor conforme o método (a tela não concatena banco/ag/cc). */
  chave: z.string().nullable(),
  emailAviso: z.string().nullable(),
})
export type PerfilDePagamentoDoFornecedor = z.infer<typeof perfilDePagamentoDoFornecedor>

/**
 * Contrato recorrente — **derivado** das CAPs `agenda='recorrente'` (M6), nunca um
 * campo digitado. O protótipo tinha um texto livre; aqui o bloco só existe quando
 * existe uma recorrência de verdade por trás dele.
 */
export const contratoDoFornecedor = z.object({
  contaPagarId: uuid,
  codigo: z.string(),
  descricao: z.string(),
  periodicidade: z.string(),
  /** `null` sem `ve_valores_financeiros`. */
  valorMensal: z.string().nullable(),
  moeda: z.string(),
  vigenciaInicio: z.string(),
  vigenciaFim: z.string().nullable(),
  proximaCompetencia: z.string().nullable(),
})
export type ContratoDoFornecedor = z.infer<typeof contratoDoFornecedor>

/**
 * O documento que a parcela de uma despesa espera antes da aprovação, lido do tipo de
 * pagamento (spec 6.4 R1, DS-555): `nf` quando o tipo exige NF (e também quando exige as
 * duas), `rpa` quando exige só o RPA. O enum mora AQUI, e não em contas-pagar.ts, porque
 * contas-pagar.ts já importa deste arquivo: o import inverso criaria um ciclo ESM, e o
 * `z.enum` chegaria indefinido na carga.
 */
export const documentoExigidoDaDespesa = z.enum(['nf', 'rpa'])
export type DocumentoExigidoDaDespesa = z.infer<typeof documentoExigidoDaDespesa>

/**
 * Uma despesa recente — **espelho de 6.4** (R2). Os campos são os mesmos que a 6.4
 * lê da mesma origem (`conta_pagar` + `parcela_pagar`), e é por isso que a suíte
 * compara as duas telas ao centavo em vez de confiar que "vieram da mesma tabela".
 */
export const despesaDoFornecedor = z.object({
  parcelaId: uuid,
  contaPagarId: uuid,
  codigo: z.string(),
  vencimento: dataNominal,
  /** `null` sem `ve_valores_financeiros`. */
  valor: z.string().nullable(),
  moeda: z.string(),
  status: z.string(),
  vencida: z.boolean(),
  /** O documento que a parcela espera: espelho da 6.4, DS-555. */
  documentoExigido: documentoExigidoDaDespesa.nullable(),
})
export type DespesaDoFornecedor = z.infer<typeof despesaDoFornecedor>

export const fornecedor = z.object({
  id: uuid,
  nome: z.string(),
  descricao: z.string().nullable(),
  parteTipo: parteDoFornecedor,
  natureza: naturezaDeFornecedor,
  ativa: z.boolean(),
  /** A parte dona — para o link 3.2 e para a edição do cadastro. */
  empresaId: uuid.nullable(),
  contatoId: uuid.nullable(),
  pais: z.string(),
  documento: z.string().nullable(),
  email: z.string().nullable(),
  telefone: z.string().nullable(),
  categoria: z.string().nullable(),
  observacoes: z.string().nullable(),
  /**
   * O prazo padrão da HABILITAÇÃO (`capacidade_fornecedor.prazo_pagamento_dias`), a fonte
   * única do prazo do fornecedor: o que o perfil de pagamento guarda de prazo é a política da
   * comissão do parceiro (D-103), nunca este. É este que sugere o vencimento da despesa nova. Viaja no topo, e não dentro do perfil, para não sumir da ficha quando o fornecedor
   * ainda não tem perfil (D-206). `null` é "sem prazo cadastrado", nunca zero.
   */
  prazoPagamentoDias: z.number().int().nullable(),
  endereco: endereco.nullable(),
  outrosPapeis: z.array(outroPapelDaParte),
  perfilPagamento: perfilDePagamentoDoFornecedor.nullable(),
  contratos: z.array(contratoDoFornecedor),
  despesas: z.array(despesaDoFornecedor),
  /**
   * O nível vê montantes? A tela precisa saber para escolher entre esconder a
   * coluna e mostrar "—" — e para não deduzir "sem valor" de um `null` que
   * significaria "sem despesa".
   */
  veValores: z.boolean(),
})
export type Fornecedor = z.infer<typeof fornecedor>

export const buscaDeFornecedores = z.object({
  q: z.string().optional(),
  categoria: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(200),
})
export type BuscaDeFornecedores = z.infer<typeof buscaDeFornecedores>

export const respostaDeFornecedores = z.object({
  data: z.array(linhaDeFornecedor),
  meta: meta.extend({
    /** As categorias EM USO — texto do cadastro, não catálogo (§Comportamentos). */
    categorias: z.array(z.string()),
    podeCriar: z.boolean(),
    podeEditar: z.boolean(),
  }),
})
export type RespostaDeFornecedores = z.infer<typeof respostaDeFornecedores>

/**
 * Uma parte que pode virar fornecedor: a Empresa (PJ ou Exterior) ou o Contato (PF) do
 * ambiente, com a capacidade que ela já tem (ativa ou não). É a lista do modal
 * "Habilitar fornecedor", que busca por rota própria do módulo de fornecedores: o
 * financeiro sem CRM busca e habilita (D-138, resposta 2 do fundador).
 */
export const parteParaHabilitar = z.object({
  parteTipo: parteDoFornecedor,
  id: uuid,
  nome: z.string(),
  natureza: naturezaDeFornecedor,
  pais: z.string(),
  /** O documento da parte, inteiro e normalizado: o CNPJ da empresa ou o documento da pessoa (D-149). */
  documento: z.string().nullable(),
  /** A habilitação que a parte já tem: nenhuma, ativa ("Já é fornecedor") ou inativa. */
  capacidade: z.object({ id: uuid, ativa: z.boolean() }).nullable(),
})
export type ParteParaHabilitar = z.infer<typeof parteParaHabilitar>

export const buscaDePartesParaHabilitar = z.object({
  q: z.string().trim().min(2, 'Digite ao menos 2 letras ou números.'),
})
export type BuscaDePartesParaHabilitar = z.input<typeof buscaDePartesParaHabilitar>

export const respostaDePartesParaHabilitar = z.object({ data: z.array(parteParaHabilitar) })
export type RespostaDePartesParaHabilitar = z.infer<typeof respostaDePartesParaHabilitar>

/** Um perfil de pagamento da parte, como a habilitação e a edição o escolhem. */
export const perfilDaParte = z.object({
  id: uuid,
  rotulo: z.string(),
  metodo: metodoDePagamento.nullable(),
  /** Já montada pelo servidor conforme o método. */
  chave: z.string().nullable(),
  emailAviso: z.string().nullable(),
  principal: z.boolean(),
})
export type PerfilDaParte = z.infer<typeof perfilDaParte>

/** O perfil novo: método e a chave da coluna daquele método (nunca da coluna de outro). */
const perfilNovo = {
  metodo: metodoDePagamento,
  chave: z.string().trim().min(1, 'Informe a chave ou a referência.'),
  emailAviso: z.string().trim().pipe(z.email({ error: 'Informe um e-mail válido.' })).nullish(),
}

/**
 * Como a habilitação resolve o perfil de pagamento, e NUNCA reescrevendo o principal:
 * usa um que a parte já tem, acrescenta outro ao lado dele, ou deixa para depois.
 */
export const escolhaDePerfilNaHabilitacao = z.discriminatedUnion('modo', [
  z.object({ modo: z.literal('existente'), perfilPagamentoId: uuid }),
  z.object({ modo: z.literal('novo'), ...perfilNovo }),
  z.object({ modo: z.literal('depois') }),
])
export type EscolhaDePerfilNaHabilitacao = z.infer<typeof escolhaDePerfilNaHabilitacao>

/**
 * Na edição pela 6.6, um quarto modo: `editar` troca SÓ método, a chave daquele método e o
 * e-mail de aviso do perfil escolhido. Primário, política de prazo da comissão, titular e as
 * colunas dos outros métodos ficam como estão.
 */
export const escolhaDePerfilNaEdicao = z.discriminatedUnion('modo', [
  z.object({ modo: z.literal('existente'), perfilPagamentoId: uuid }),
  z.object({ modo: z.literal('novo'), ...perfilNovo }),
  z.object({ modo: z.literal('editar'), perfilPagamentoId: uuid, ...perfilNovo }),
])
export type EscolhaDePerfilNaEdicao = z.infer<typeof escolhaDePerfilNaEdicao>

/** Os termos da habilitação: o que é do fornecedor, e não da parte. */
export const termosDoFornecedor = z.object({
  categoria: z.string().trim().nullish(),
  email: z.string().nullish(),
  telefone: z.string().nullish(),
  /** Dias corridos que a DV tem para pagar depois da fatura. Nunca vira política de comissão (D-103). */
  prazoPagamentoDias: z.number().int().min(0).max(365).nullish(),
  observacoes: z.string().nullish(),
})
export type TermosDoFornecedor = z.input<typeof termosDoFornecedor>

/**
 * "Habilitar fornecedor" (D-138, DS-535): a parte existente é o caminho normal; a parte
 * nova vem pelos MESMOS contratos da Empresa (3.3) e do Contato (3.4), e nasce na mesma
 * transação da habilitação (DS-265). Parte já habilitada não é erro: o servidor devolve o
 * estado. Parte com habilitação inativa só volta com `reativar: true`.
 */
export const entradaDeHabilitacaoDeFornecedor = z.object({
  parte: z.discriminatedUnion('modo', [
    z.object({ modo: z.literal('existente'), parteTipo: parteDoFornecedor, parteId: uuid }),
    z.object({ modo: z.literal('nova_empresa'), empresa: entradaDeNovaEmpresa }),
    z.object({ modo: z.literal('novo_contato'), contato: entradaDeNovoContato }),
  ]),
  termos: termosDoFornecedor.default({}),
  perfil: escolhaDePerfilNaHabilitacao.default({ modo: 'depois' }),
  reativar: z.boolean().default(false),
}).refine((v) => v.parte.modo === 'existente' || v.perfil.modo !== 'existente', {
  path: ['perfil'],
  message: 'Parte nova ainda não tem perfil: acrescente um ou informe depois.',
})
export type EntradaDeHabilitacaoDeFornecedor = z.input<typeof entradaDeHabilitacaoDeFornecedor>

export const estadoDaHabilitacao = z.enum(['criado', 'ja_habilitado', 'reativado'])
export type EstadoDaHabilitacao = z.infer<typeof estadoDaHabilitacao>

export const resultadoDaHabilitacao = z.object({
  /** O uuid da capacidade: a chave de `6.6?id=` e da despesa. */
  id: uuid,
  estado: estadoDaHabilitacao,
  parteCriada: z.boolean(),
})
export type ResultadoDaHabilitacao = z.infer<typeof resultadoDaHabilitacao>

/**
 * A edição toca a capacidade e o perfil, nunca a parte. `ativa` só aceita `false`:
 * reativar é ato explícito do POST com `reativar: true` (DS-535).
 */
export const entradaDeEdicaoDeFornecedor = termosDoFornecedor.extend({
  ativa: z.literal(false).optional(),
  perfil: escolhaDePerfilNaEdicao.optional(),
})
export type EntradaDeEdicaoDeFornecedor = z.input<typeof entradaDeEdicaoDeFornecedor>
